import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { X, Search } from "lucide-react";
import { FiEdit3, FiMessageSquare } from "react-icons/fi";
import "../style/search-modal.css";

export default function ChatSearchModal({
  isOpen,
  onClose,
  chats = [],
  activeChatId,
  onSelectChat,
  onNewChat,
}) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  // Auto focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 50);
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Filtered chats based on search query
  const filteredChats = useMemo(() => {
    if (!query.trim()) return chats;
    const q = query.toLowerCase().trim();
    return chats.filter((c) => (c.title || "Untitled Chat").toLowerCase().includes(q));
  }, [chats, query]);

  // Group filtered or all chats by date
  const groupedChats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(startOfToday.getTime() - 30 * 24 * 60 * 60 * 1000);

    const groups = {
      today: [],
      previous7Days: [],
      previous30Days: [],
      older: [],
    };

    filteredChats.forEach((chat) => {
      const chatDate = new Date(chat.updatedAt || chat.createdAt || now);
      if (chatDate >= startOfToday) {
        groups.today.push(chat);
      } else if (chatDate >= sevenDaysAgo) {
        groups.previous7Days.push(chat);
      } else if (chatDate >= thirtyDaysAgo) {
        groups.previous30Days.push(chat);
      } else {
        groups.older.push(chat);
      }
    });

    return groups;
  }, [filteredChats]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="chat-search-overlay" onClick={onClose}>
      <div className="chat-search-card" onClick={(e) => e.stopPropagation()}>
        {/* Header Search Input */}
        <div className="chat-search-header">
          <input
            ref={inputRef}
            type="text"
            className="chat-search-input"
            placeholder="Search chats..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="button"
            className="chat-search-close-btn"
            onClick={onClose}
            aria-label="Close search"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search Results / Chat List */}
        <div className="chat-search-body">
          {/* New Chat Item */}
          <button
            type="button"
            className="chat-search-item chat-search-new-chat"
            onClick={() => {
              onNewChat();
              onClose();
            }}
          >
            <FiEdit3 className="chat-search-item-icon" size={17} />
            <span className="chat-search-item-title">New chat</span>
          </button>

          {/* Results when searching or empty query */}
          {filteredChats.length === 0 && query.trim() ? (
            <div className="chat-search-empty">
              <Search size={22} className="chat-search-empty-icon" />
              <p>No matching chats found for "{query}"</p>
            </div>
          ) : (
            <>
              {/* Today Section */}
              {groupedChats.today.length > 0 && (
                <div className="chat-search-group">
                  <div className="chat-search-group-title">Today</div>
                  {groupedChats.today.map((chat) => (
                    <button
                      key={chat._id}
                      type="button"
                      className="chat-search-item"
                      onClick={() => {
                        onSelectChat(chat._id);
                        onClose();
                      }}
                    >
                      <FiMessageSquare className="chat-search-item-icon" size={16} />
                      <span className="chat-search-item-title">{chat.title || "Untitled Chat"}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Previous 7 Days Section */}
              {groupedChats.previous7Days.length > 0 && (
                <div className="chat-search-group">
                  <div className="chat-search-group-title">Previous 7 Days</div>
                  {groupedChats.previous7Days.map((chat) => (
                    <button
                      key={chat._id}
                      type="button"
                      className="chat-search-item"
                      onClick={() => {
                        onSelectChat(chat._id);
                        onClose();
                      }}
                    >
                      <FiMessageSquare className="chat-search-item-icon" size={16} />
                      <span className="chat-search-item-title">{chat.title || "Untitled Chat"}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Previous 30 Days Section */}
              {groupedChats.previous30Days.length > 0 && (
                <div className="chat-search-group">
                  <div className="chat-search-group-title">Previous 30 Days</div>
                  {groupedChats.previous30Days.map((chat) => (
                    <button
                      key={chat._id}
                      type="button"
                      className="chat-search-item"
                      onClick={() => {
                        onSelectChat(chat._id);
                        onClose();
                      }}
                    >
                      <FiMessageSquare className="chat-search-item-icon" size={16} />
                      <span className="chat-search-item-title">{chat.title || "Untitled Chat"}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Older Section */}
              {groupedChats.older.length > 0 && (
                <div className="chat-search-group">
                  <div className="chat-search-group-title">Older</div>
                  {groupedChats.older.map((chat) => (
                    <button
                      key={chat._id}
                      type="button"
                      className="chat-search-item"
                      onClick={() => {
                        onSelectChat(chat._id);
                        onClose();
                      }}
                    >
                      <FiMessageSquare className="chat-search-item-icon" size={16} />
                      <span className="chat-search-item-title">{chat.title || "Untitled Chat"}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
